// src/pages/PlateSelector.js
import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import API_ENDPOINTS from "../apiConfig";
import Notification from "./Notification";
import ConfirmModal from "./ConfirmModal";
import { authFetch } from "../utils/authFetch";
import { useSite } from "../context/SiteContext";
import { contrastColor } from "../utils/colorUtils";
import "../style/ezentry.css";

// syncStatusByPlate: see usePlateSyncStatus (polled by the parent so the info box can share it)
// syncFilterActive/onToggleSyncFilter: "only plates with a sync warning" filter, toggled from the parent's info box too
function PlateSelector({ syncStatusByPlate = {}, syncFilterActive = false, onToggleSyncFilter = () => {} }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const viewDropdownRef = useRef(null);
  const today = new Date().toISOString().split("T")[0];

  // Context holen
  const { platesByArea, setPlatesByArea, selectedSite, setSites, siteId, areaId } = useSite();
  // These are the categories specific to this site
  const availableCategories = selectedSite?.available_categories || [];

  const mergeNewCategories = (cats) => {
    if (!cats?.length) return;
    setSites(prev => prev.map(s => {
      if (s.id !== siteId) return s;
      const existing = s.available_categories || [];
      const existingIds = new Set(existing.map(c => c.id));
      const toAdd = cats.filter(c => !existingIds.has(c.id));
      if (!toAdd.length) return s;
      return { ...s, available_categories: [...existing, ...toAdd] };
    }));
  };

  const [activeFilters, setActiveFilters] = useState([]); // For filtering the list
  const [selectedTags, setSelectedTags] = useState([]); // For the Add/Edit form

  const [sortKey, setSortKey] = useState(() => localStorage.getItem("plates_sortKey") ?? "plate");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("plates_sortDir") ?? "asc");
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("plates_groupBy") ?? "category");
  const [expandedGroups, setExpandedGroups] = useState(new Set(["__none__"]));
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);

  useEffect(() => {
    setExpandedGroups(new Set(["__none__"]));
  }, [groupBy]);

  useEffect(() => {
    if (!viewDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(e.target)) {
        setViewDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [viewDropdownOpen]);

  const toggleCollapsed = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  useEffect(() => {
    localStorage.setItem("plates_sortKey", sortKey);
    localStorage.setItem("plates_sortDir", sortDir);
    if (groupBy) localStorage.setItem("plates_groupBy", groupBy);
    else localStorage.removeItem("plates_groupBy");
  }, [sortKey, sortDir, groupBy]);

  const [isCreatingCat, setIsCreatingCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  //const { platesByArea, setPlatesByArea, selectedSite, areaId } = useSite();

  const plates = platesByArea[areaId] || [];

  // Lokaler State für UI
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!inputValue) setExpandedGroups(new Set(["__none__"]));
  }, [inputValue]);
  const [isAdding, setIsAdding] = useState(false);
  const [isSavingNew, setIsSavingNew] = useState(false);
  const [newData, setNewData] = useState({ plate: "", valid_from: today, valid_until: "", note: "" });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ plate: "", valid_from: "", valid_until: "", note: "", categories: [] });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [plateToDelete, setPlateToDelete] = useState(null);

  // ---------------- Bulk selection ----------------
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditFromEnabled, setBulkEditFromEnabled] = useState(false);
  const [bulkEditFrom, setBulkEditFrom] = useState(today);
  const [bulkEditUntilEnabled, setBulkEditUntilEnabled] = useState(false);
  const [bulkEditUntil, setBulkEditUntil] = useState("");
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const pressTimerRef = useRef(null);
  const pressStartRef = useRef(null);
  const justLongPressedRef = useRef(false);

  const toggleSelected = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
    setSelectMode(next.size > 0);
  };

  const selectGroupIds = (ids, on) => {
    const next = new Set(selectedIds);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    setSelectedIds(next);
    setSelectMode(next.size > 0);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handlePointerDownCard = (e, plateId) => {
    if (selectMode) return; // already selecting — a plain tap toggles instead
    pressStartRef.current = [e.clientX, e.clientY];
    pressTimerRef.current = setTimeout(() => {
      justLongPressedRef.current = true;
      toggleSelected(plateId);
    }, 480);
  };
  const handlePointerMoveCard = (e) => {
    if (!pressStartRef.current) return;
    const [x, y] = pressStartRef.current;
    if (Math.hypot(e.clientX - x, e.clientY - y) > 10) {
      clearTimeout(pressTimerRef.current);
    }
  };
  const clearPressCard = () => {
    clearTimeout(pressTimerRef.current);
    pressStartRef.current = null;
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const res = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error("Failed to delete plates");
      const result = await res.json().catch(() => null);
      const deletedCount = result?.deleted ?? ids.length;

      setPlatesByArea((prev) => ({
        ...prev,
        [areaId]: (prev[areaId] || []).filter((p) => !selectedIds.has(p.id)),
      }));

      setSuccessMessage(t("Delete_success_bulk", { count: deletedCount }));
      setShowBulkConfirm(false);
      exitSelectMode();
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  const resetBulkEditForm = () => {
    setShowBulkEdit(false);
    setBulkEditFromEnabled(false);
    setBulkEditUntilEnabled(false);
    setBulkEditFrom(today);
    setBulkEditUntil("");
  };

  const handleBulkEditDates = async () => {
    if (!bulkEditFromEnabled && !bulkEditUntilEnabled) return; // nothing opted in — nothing to send
    const ids = Array.from(selectedIds);
    if (!ids.length) return;

    setBulkEditSaving(true);
    try {
      const body = { ids };
      if (bulkEditFromEnabled) body.valid_from = bulkEditFrom;
      if (bulkEditUntilEnabled) body.valid_until = bulkEditUntil || null; // blank = no expiry

      const res = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to update plates");
      const result = await res.json().catch(() => null);
      const updatedCount = result?.updated ?? ids.length;
      const skipped = result?.skipped ?? [];
      const skippedIds = new Set(skipped.map((p) => p.id));

      // only apply the new date(s) locally to plates that actually got written —
      // a skipped plate keeps whatever it already had
      setPlatesByArea((prev) => ({
        ...prev,
        [areaId]: (prev[areaId] || []).map((p) =>
          selectedIds.has(p.id) && !skippedIds.has(p.id)
            ? {
                ...p,
                ...(bulkEditFromEnabled ? { valid_from: bulkEditFrom } : {}),
                ...(bulkEditUntilEnabled ? { valid_until: bulkEditUntil || null } : {}),
              }
            : p
        ),
      }));

      setSuccessMessage(t("plates_updated_bulk", { count: updatedCount }));

      if (skipped.length > 0) {
        const shown = skipped.slice(0, 5).map((p) => p.plate).join(", ");
        const rest = skipped.length > 5 ? ", " + t("and_n_more", { count: skipped.length - 5 }) : "";
        setErrorMessage(t("plates_skipped_invalid_range", { count: skipped.length, plates: shown + rest }));
        // leave just the problem plates selected so they're easy to find and fix by hand
        setSelectedIds(skippedIds);
      } else {
        exitSelectMode();
      }
      resetBulkEditForm();
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setBulkEditSaving(false);
    }
  };

  // ---------------- Focus Input bei Add ----------------
  useLayoutEffect(() => {
    if (isAdding) inputRef.current?.focus();
  }, [isAdding]);

  // ---------------- Helpers ----------------
  const formatPlate = (value) => {
    let formatted = value.toUpperCase().replace(/[^A-Z0-9ÄÖÜ-]/gi, "");
    const firstHyphen = formatted.indexOf("-");
    if (firstHyphen !== -1) {
      formatted = formatted.slice(0, firstHyphen + 1) + formatted.slice(firstHyphen + 1).replace(/-/g, "");
    }
    return formatted;
  };

  const filteredPlates = plates.filter((p) => {
    const query = inputValue.toLowerCase();
    const matchesSearch = (p?.plate ?? "").toLowerCase().includes(query) || (p?.note ?? "").toLowerCase().includes(query);
    
    // Check if plate has the active category ID in its categories array
    const matchesCategory = activeFilters.length === 0 || activeFilters.every(id => p.categories?.some(c => c.id === id));
    
    const matchesSync = !syncFilterActive || (syncStatusByPlate[p.id] || []).some(e => e.display === "warning");

    return matchesSearch && matchesCategory && matchesSync;
  });

  const sortedPlates = [...filteredPlates].sort((a, b) => {
    let valA, valB;
    if (sortKey === "plate") {
      valA = (a.plate ?? "").toUpperCase();
      valB = (b.plate ?? "").toUpperCase();
    } else if (sortKey === "created_at") {
      valA = a.created_at ? new Date(a.created_at).getTime() : 0;
      valB = b.created_at ? new Date(b.created_at).getTime() : 0;
    } else if (sortKey === "updated_at") {
      valA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      valB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    } else if (sortKey === "valid_until") {
      valA = a.valid_until ? new Date(a.valid_until).getTime() : Infinity;
      valB = b.valid_until ? new Date(b.valid_until).getTime() : Infinity;
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const expiryBuckets = [
    { key: "expired", label: t("Expired"),             color: "#dc3545" },
    { key: "month",   label: t("expiry_this_month"),   color: "#e8600a" },
    { key: "quarter", label: t("expiry_this_quarter"), color: "#0d6efd" },
    { key: "later",   label: t("expiry_later"),        color: "#6c757d" },
    { key: "never",   label: t("expiry_never"),        color: "#198754" },
  ];

  const addedBuckets = [
    { key: "today",  label: t("added_today"),        color: "#198754" },
    { key: "week",   label: t("added_last_7_days"),  color: "#0d6efd" },
    { key: "month",  label: t("added_last_30_days"), color: "#e8600a" },
    { key: "older",  label: t("added_older"),        color: "#6c757d" },
  ];

  const getAddedBucket = (plate) => {
    if (!plate.created_at) return "older";
    const created = dayjs(plate.created_at);
    const now = dayjs();
    if (created.isSame(now, "day")) return "today";
    if (created.isAfter(now.subtract(7, "day").startOf("day"))) return "week";
    if (created.isAfter(now.subtract(30, "day").startOf("day"))) return "month";
    return "older";
  };

  const getExpiryBucket = (plate) => {
    if (!plate.valid_until) return "never";
    const exp = dayjs(plate.valid_until);
    const now = dayjs();
    if (exp.isBefore(now, "day")) return "expired";
    if (!exp.isAfter(now.endOf("month"), "day")) return "month";
    const qEndMonth = (Math.floor(now.month() / 3) + 1) * 3 - 1;
    const qEnd = dayjs().month(qEndMonth).endOf("month");
    if (!exp.isAfter(qEnd, "day")) return "quarter";
    return "later";
  };

const groupedPlates = (() => {
    if (!groupBy) return null;

    if (groupBy === "category") {
      const map = {};
      sortedPlates.forEach(plate => {
        if (!plate.categories || plate.categories.length === 0) {
          if (!map["__none__"]) map["__none__"] = { key: "__none__", label: t("uncategorized"), color: "#6c757d", plates: [] };
          map["__none__"].plates.push(plate);
        } else {
          plate.categories.forEach(cat => {
            if (!map[cat.id]) map[cat.id] = { key: cat.id, label: cat.name, color: cat.color, plates: [] };
            map[cat.id].plates.push(plate);
          });
        }
      });
      const named = Object.values(map).filter(g => g.key !== "__none__").sort((a, b) => a.label.localeCompare(b.label));
      if (map["__none__"]) named.push(map["__none__"]);
      return named;
    }

    if (groupBy === "expiry") {
      const map = {};
      expiryBuckets.forEach(b => { map[b.key] = { ...b, plates: [] }; });
      sortedPlates.forEach(plate => { map[getExpiryBucket(plate)].plates.push(plate); });
      return expiryBuckets.map(b => map[b.key]).filter(g => g.plates.length > 0);
    }

    if (groupBy === "added") {
      const map = {};
      addedBuckets.forEach(b => { map[b.key] = { ...b, plates: [] }; });
      sortedPlates.forEach(plate => { map[getAddedBucket(plate)].plates.push(plate); });
      return addedBuckets.map(b => map[b.key]).filter(g => g.plates.length > 0);
    }

    return null;
  })();

  // the sync filter exists to show you those plates — don't leave them inside collapsed groups
  useEffect(() => {
    setExpandedGroups(syncFilterActive && groupedPlates
      ? new Set(groupedPlates.map(g => g.key))
      : new Set(["__none__"]));
  }, [syncFilterActive]);

  const singleGroupKey = groupedPlates?.length === 1 ? groupedPlates[0].key : null;
  useEffect(() => {
    if (singleGroupKey) setExpandedGroups(prev => new Set([...prev, singleGroupKey]));
  }, [singleGroupKey]);

  // ---------------- Input Handler ----------------
  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleSortChange = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // ---------------- Add New ----------------
  const handleAddNew = () => {
    setIsAdding(true);
    setNewData({ plate: formatPlate(inputValue), valid_from: today, valid_until: "", note: "" });
  };

  // Duplicate check ignores hyphens, same as the backend's — "W-1234A" and
  // "W1234A" are the same plate for this purpose, even though we store
  // whichever formatting the user actually typed.
  const stripFormatting = (p) => (p || "").toUpperCase().replace(/-/g, "");

  const handleSaveNew = async () => {
    // Guards against a double-fire (fast double-click, or a resent request)
    // triggering two overlapping submits — the backend's own duplicate check
    // is a check-then-insert race and can't reliably catch two requests this
    // close together, so this has to be stopped at the source.
    if (isSavingNew) return;

    const normalizedNew = stripFormatting(newData.plate);
    const duplicate = plates.find((p) => stripFormatting(p.plate) === normalizedNew);
    if (duplicate) {
      setErrorMessage(t("plate_already_exists"));
      return;
    }

    setIsSavingNew(true);
    try {
      const res = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newData, categories: selectedTags }), // Send tags to DB
      });

      if (!res.ok) throw new Error(res.status === 409 ? t("plate_already_exists") : "Failed to add plate");

      const created = await res.json().catch(() => null);

      mergeNewCategories(created?.categories);
      const safePlate = {
        id: created?.id ?? `temp-${Date.now()}`,
        plate: created?.plate ?? newData.plate,
        valid_from: created?.valid_from ?? newData.valid_from,
        valid_until: created?.valid_until ?? newData.valid_until,
        note: created?.note ?? newData.note,
        categories: created?.categories ?? [],
      };

      setPlatesByArea((prev) => ({
        ...prev,
        [areaId]: [...(prev[areaId] || []), safePlate],
      }));

      // Reset UI
      setIsAdding(false);
      setSelectedTags([]); // Clear tags
      setInputValue("");
      setSuccessMessage(t("Save_success"));
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsSavingNew(false);
    }
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewData({ plate: "", valid_from: today, valid_until: "", note: "" });
  };

  // ---------------- Edit ----------------
  const handleToggleEdit = (plate) => {
    const isEditing = editingId === plate.id;
    setEditingId(isEditing ? null : plate.id);

    if (!isEditing) {
      // Populate form data
      setEditData({ 
        plate: plate.plate, 
        valid_from: plate.valid_from || "", 
        valid_until: plate.valid_until || "", 
        note: plate.note || "",
        categories: plate.categories || []
      });
      
      // PRE-SELECT: Set the tags from the plate's categories array
      setSelectedTags(plate.categories?.map(c => c.name) || []);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const res = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates/${editingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...editData, categories: selectedTags }), // Send updated tags
      });
      
      if (!res.ok) throw new Error("Failed to save plate");

      const updated = await res.json().catch(() => null);
      mergeNewCategories(updated?.categories);

      setPlatesByArea((prev) => ({
        ...prev,
        [areaId]: prev[areaId].map((p) =>
          p.id === editingId
            ? { ...p, ...editData, categories: updated?.categories ?? [] }
            : p
        ),
      }));

      setEditingId(null);
      setSelectedTags([]); // Clear tags
      setSuccessMessage(t("Save_success"));
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  // ---------------- Delete ----------------
  const handleDelete = async (plate) => {
    if (!plate) return;
    try {
      const res = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates/${plate.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Failed to delete plate");

      setPlatesByArea((prev) => ({
        ...prev,
        [areaId]: prev[areaId].filter((p) => p.id !== plate.id),
      }));

      setSuccessMessage(t("Delete_success"));
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  const toggleTag = (name) => {
    setSelectedTags(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const handleAddCustomTag = async () => {
    const name = newCatName.trim();
    if (!name) return;

    // If it already exists locally, just toggle it on — no round-trip needed
    const existing = availableCategories.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      setSelectedTags(prev => prev.includes(existing.name) ? prev : [...prev, existing.name]);
      setNewCatName('');
      setIsCreatingCat(false);
      return;
    }

    try {
      const url = API_ENDPOINTS.createCategory.replace('##siteid##', selectedSite.id);
      const res = await authFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error('Failed to create category');
      const cat = await res.json();
      mergeNewCategories([cat]);
      setSelectedTags(prev => prev.includes(cat.name) ? prev : [...prev, cat.name]);
    } catch (err) {
      setErrorMessage(err.message);
    }

    setNewCatName('');
    setIsCreatingCat(false);
  };

  // ---------------- Render ----------------
  return (
    <div className="container mt-4">
      <h4 className="mb-3">{t("Select Car Plate")}</h4>

      {/* Search + Add + View options */}
      <div className="d-flex gap-2 mb-2 align-items-center">
        <div className="input-group flex-grow-1">
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={t("search_plates_placeholder")}
            value={inputValue}
            onChange={handleInputChange}
          />
          {inputValue && (
            <button className="btn btn-outline-secondary" onClick={() => setInputValue("")}>
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
        <button className="btn ez-button-full flex-shrink-0" onClick={handleAddNew}>
          <i className="bi bi-plus-lg me-1"></i>
          <span className="d-none d-sm-inline">{t("Add plate")}</span>
          <span className="d-sm-none">{t("new_short")}</span>
        </button>
        {plates.length > 0 && (() => {
          const isCustomized = sortKey !== "plate" || sortDir !== "asc" || groupBy !== null;
          const sortLabels = { plate: t("sort_plate"), created_at: t("sort_created"), valid_until: t("sort_expiry") };
          return (
            <div className="position-relative flex-shrink-0" ref={viewDropdownRef}>
              <button
                className="btn btn-outline-secondary"
                style={isCustomized ? { borderColor: "var(--ez-orange)", color: "var(--ez-orange)", background: "#fff3eb" } : {}}
                onClick={() => setViewDropdownOpen(p => !p)}
                title={t("sort_by")}
              >
                <i className="bi bi-sliders2"></i>
                <span className="ms-1 d-none d-sm-inline" style={{ fontSize: "0.82rem" }}>
                  {sortLabels[sortKey]}
                  <i className={`bi bi-arrow-${sortDir === "asc" ? "up" : "down"} ms-1`}></i>
                  {groupBy && <i className={`bi ${{ category: "bi-collection", expiry: "bi-calendar-range", added: "bi-clock-history" }[groupBy]} ms-2`}></i>}
                </span>
              </button>
              {viewDropdownOpen && (
                <div style={{
                  position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 1050,
                  background: "var(--ez-card-bg, #fff)", border: "1px solid var(--bs-border-color)",
                  borderRadius: "10px", padding: "12px 8px", minWidth: "190px",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.12)"
                }}>
                  <div className="px-2 mb-1"><small className="text-muted fw-semibold" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("sort_by")}</small></div>
                  {[
                    { key: "plate",       label: t("sort_plate")   },
                    { key: "created_at",  label: t("sort_created") },
                    { key: "valid_until", label: t("sort_expiry")  },
                  ].map(({ key, label }) => {
                    const isActive = sortKey === key;
                    return (
                      <button key={key}
                        className="d-flex align-items-center w-100 btn btn-sm text-start px-2 py-1 rounded"
                        style={{ background: isActive ? "#fff3eb" : "transparent", color: isActive ? "var(--ez-orange)" : "inherit", fontSize: "0.85rem" }}
                        onClick={() => handleSortChange(key)}
                      >
                        <i className={`bi bi-check2 me-2 ${isActive ? "" : "invisible"}`}></i>
                        {label}
                        {isActive && <i className={`bi bi-arrow-${sortDir === "asc" ? "up" : "down"} ms-auto`}></i>}
                      </button>
                    );
                  })}
                  <hr className="my-2" />
                  <div className="px-2 mb-1"><small className="text-muted fw-semibold" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("view_group")}</small></div>
                  {[
                    { key: "category", label: t("group_by_category"), icon: "bi-collection"    },
                    { key: "expiry",   label: t("group_by_expiry"),   icon: "bi-calendar-range" },
                    { key: "added",    label: t("group_by_added"),    icon: "bi-clock-history"  },
                  ].map(({ key, label, icon }) => {
                    const isActive = groupBy === key;
                    return (
                      <button key={key}
                        className="d-flex align-items-center w-100 btn btn-sm text-start px-2 py-1 rounded"
                        style={{ background: isActive ? "#fff3eb" : "transparent", color: isActive ? "var(--ez-orange)" : "inherit", fontSize: "0.85rem" }}
                        onClick={() => setGroupBy(prev => prev === key ? null : key)}
                      >
                        <i className={`bi ${isActive ? "bi-check2" : icon} me-2`}></i>
                        {label}
                      </button>
                    );
                  })}
                  <hr className="my-2" />
                  <button
                    className="d-flex align-items-center w-100 btn btn-sm text-start px-2 py-1 rounded"
                    style={{ fontSize: "0.85rem" }}
                    onClick={() => {
                      selectMode ? exitSelectMode() : setSelectMode(true);
                      setViewDropdownOpen(false);
                    }}
                  >
                    <i className={`bi ${selectMode ? "bi-x-square" : "bi-check2-square"} me-2`}></i>
                    {selectMode ? t("done_selecting") : t("select_multiple")}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Category Filter Chips */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {availableCategories.map((cat) => (
          <button
            key={cat.id}
            className="btn btn-sm rounded-pill border"
            style={{
              backgroundColor: activeFilters.includes(cat.id) ? cat.color : "transparent",
              color: activeFilters.includes(cat.id) ? contrastColor(cat.color) : "inherit",
              borderColor: cat.color
            }}
            onClick={() => setActiveFilters(prev => prev.includes(cat.id) ? prev.filter(id => id !== cat.id) : [...prev, cat.id])}
          >
            {cat.name}
          </button>
        ))}
        {syncFilterActive && (
          <button
            id="sync-filter-chip"
            className="btn btn-sm rounded-pill border border-warning bg-warning-subtle text-warning-emphasis"
            onClick={onToggleSyncFilter}
          >
            <i className="bi bi-exclamation-triangle-fill me-1"></i>{t("sync_filter_chip")}
            <i className="bi bi-x-lg ms-2"></i>
          </button>
        )}
        {activeFilters.length > 0 && (
          <button
            className="btn btn-sm rounded-pill border"
            style={{ borderColor: "#6c757d", color: "#6c757d" }}
            onClick={() => setActiveFilters([])}
          >
            <i className="bi bi-x-lg me-1"></i>{t("Clear filters")}
          </button>
        )}
      </div>

      {/* Add new form */}
      {isAdding && (
        <div className="mb-3 border ez-rounded-tile bg-white ez-shadow">
          {/* Primary field — card surface */}
          <div className="p-3">
            <label>{t("Plate")}</label>
            <input ref={inputRef} type="text" className="form-control" value={newData.plate} onChange={(e) => setNewData({ ...newData, plate: formatPlate(e.target.value) })} />
          </div>
          {/* Secondary fields — recessed tray */}
          <div className="p-3 border-top bg-light">
            <div className="row g-2 mb-2">
              <div className="col-6">
                <label>{t("Valid From")}</label>
                <input type="date" className="form-control" value={newData.valid_from} onChange={(e) => setNewData({ ...newData, valid_from: e.target.value })} />
              </div>
              <div className="col-6">
                <label>{t("Valid Until")}</label>
                <input type="date" className="form-control" value={newData.valid_until} onChange={(e) => setNewData({ ...newData, valid_until: e.target.value })} />
              </div>
            </div>
            <div className="mb-2">
              <label>{t("Note")}</label>
              <input type="text" className="form-control" value={newData.note} onChange={(e) => setNewData({ ...newData, note: e.target.value })} />
              <div className="mt-1 text-muted" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-shield-exclamation me-1"></i>
                {t("note_gdpr_hint")}
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex gap-1 flex-wrap align-items-center">
                {availableCategories.map(cat => (
                  <span
                    key={cat.id}
                    onClick={() => toggleTag(cat.name)}
                    className="badge"
                    style={{
                      backgroundColor: selectedTags.includes(cat.name) ? cat.color : '#dee2e6',
                      color: selectedTags.includes(cat.name) ? contrastColor(cat.color) : '#6c757d',
                      cursor: "pointer"
                    }}
                  >
                    {cat.name}
                  </span>
                ))}
                {!isCreatingCat ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm rounded-pill py-0 px-2"
                    onClick={() => setIsCreatingCat(true)}
                  >
                    <i className="bi bi-plus"></i> {t("new_category")}
                  </button>
                ) : (
                  <div className="input-group input-group-sm" style={{ width: '150px' }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={newCatName}
                      autoFocus
                      onChange={(e) => setNewCatName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleAddCustomTag(); }
                        if (e.key === 'Escape') setIsCreatingCat(false);
                      }}
                    />
                    <button className="btn btn-success" onClick={handleAddCustomTag}><i className="bi bi-check"></i></button>
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn ez-button-full btn-sm" onClick={handleSaveNew} disabled={isSavingNew}>{t("Save")}</button>
              <button className="btn btn-secondary btn-sm" onClick={handleCancelNew} disabled={isSavingNew}>{t("Cancel")}</button>
            </div>
          </div>
        </div>
      )}

      <Notification message={errorMessage} type="danger" onClose={() => setErrorMessage("")} />
      <Notification message={successMessage} type="success" onClose={() => setSuccessMessage("")} />

      {/* No results — typed search, no filter pills: offer to add */}
      {sortedPlates.length === 0 && plates.length > 0 && inputValue && activeFilters.length === 0 && !syncFilterActive && !isAdding && (
        <div className="text-center py-3">
          <p className="text-muted mb-2">{t("no_plate_found_for", { plate: inputValue.toUpperCase() })}</p>
          <button className="btn btn-sm ez-button-full" onClick={handleAddNew}>
            <i className="bi bi-plus-lg me-1"></i>{t("Add plate")}
          </button>
        </div>
      )}

      {/* No results — filter pills active: don't offer add (plate may exist but be filtered out) */}
      {sortedPlates.length === 0 && plates.length > 0 && (activeFilters.length > 0 || syncFilterActive) && (
        <div className="text-muted text-center py-3">
          <i className="bi bi-funnel me-1"></i>{t("No plates match your filter")}
        </div>
      )}

      {/* Empty area hint */}
      {plates.length === 0 && !isAdding && (
        <div className="text-center py-4">
          <p className="text-muted mb-2">{t("No plates in this area yet.")}</p>
          <button className="btn btn-sm ez-button-full" onClick={handleAddNew}>
            <i className="bi bi-plus-lg me-1"></i>{t("Add first plate")}
          </button>
        </div>
      )}

      {/* Plate list */}
      <div className={`ez-plates${selectMode ? " ez-selecting" : ""}`}>
      {(() => {
        const renderPlateCard = (plate) => {
          const isExpired = plate.valid_until && dayjs(plate.valid_until).isBefore(dayjs(), "day");
          const isEditing = editingId === plate.id;
          const isChecked = selectedIds.has(plate.id);
          const syncEntries = syncStatusByPlate[plate.id] || [];
          const hasSyncWarning = syncEntries.some(e => e.display === "warning");
          return (
            <div
              key={plate.id}
              className={`mb-2 border ez-rounded-tile ez-shadow bg-white ez-plate-card${isChecked ? " ez-checked" : ""}`}
            >
              <div
                className="d-flex justify-content-between align-items-center p-2"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (justLongPressedRef.current) { justLongPressedRef.current = false; return; }
                  if (selectMode) toggleSelected(plate.id);
                  else handleToggleEdit(plate);
                }}
                onPointerDown={(e) => handlePointerDownCard(e, plate.id)}
                onPointerMove={handlePointerMoveCard}
                onPointerUp={clearPressCard}
                onPointerLeave={clearPressCard}
                onPointerCancel={clearPressCard}
              >
                <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="ez-check-slot"
                    onClick={(e) => { e.stopPropagation(); toggleSelected(plate.id); }}
                  >
                    <div
                      style={{
                        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                        border: `2px solid ${isChecked ? "var(--ez-orange)" : "#dee2e6"}`,
                        background: isChecked ? "var(--ez-orange)" : "var(--ez-card-bg, #fff)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {isChecked && (
                        <svg width="11" height="11" viewBox="0 0 16 16">
                          <path d="M2 8l4 4 8-9" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                <div style={{ minWidth: 0 }}>
                  <span className={isExpired ? "fw-bold text-danger" : ""}>{plate.plate}</span>
                  {isExpired && <small className="text-danger ms-1">({t("Expired")})</small>}
                  {hasSyncWarning && (
                    <OverlayTrigger
                      trigger={["hover", "click"]}
                      placement="bottom"
                      rootClose
                      overlay={
                        <Tooltip>
                          <div className="text-start">
                            <strong>{t("sync_status_title")}</strong>
                            {syncEntries
                              .filter(e => e.display !== "pending")
                              .map((e, idx) => {
                                const ok = e.display === "ok";
                                const ts = ok ? e.synced_at : e.last_attempt_at;
                                return (
                                  <div key={idx} className="mt-1">
                                    <i className={`bi ${ok ? "bi-check-lg text-success" : "bi-x-lg text-danger"} me-1`}></i>
                                    {e.gate_name || (e.gate_direction ? t(e.gate_direction) : "")}
                                    {ts && (
                                      <div className="small">
                                        {dayjs(ts).format("DD.MM.YYYY HH:mm")}
                                        {!ok && ` (${t("last try")})`}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </Tooltip>
                      }
                    >
                      <i
                        className="bi bi-exclamation-triangle text-warning ms-1"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => e.stopPropagation()}
                      ></i>
                    </OverlayTrigger>
                  )}

                  <div className="small text-muted">{plate.note}</div>

                  <div className="d-flex gap-1 flex-wrap mt-1 align-items-center">
                    {isEditing ? (
                      <>
                        {availableCategories.map(cat => (
                          <span
                            key={cat.id}
                            onClick={(e) => { e.stopPropagation(); toggleTag(cat.name); }}
                            className="badge rounded-pill"
                            style={{
                              backgroundColor: selectedTags.includes(cat.name) ? cat.color : '#dee2e6',
                              color: selectedTags.includes(cat.name) ? 'white' : '#6c757d',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                          >
                            {cat.name}
                          </span>
                        ))}
                        {!isCreatingCat ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm rounded-pill py-0 px-2"
                            style={{ fontSize: '0.75rem' }}
                            onClick={(e) => { e.stopPropagation(); setIsCreatingCat(true); }}
                          >
                            <i className="bi bi-plus"></i> {t("new_category")}
                          </button>
                        ) : (
                          <div className="input-group input-group-sm" style={{ width: '150px' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              placeholder={t('new_category')}
                              className="form-control form-control-sm"
                              value={newCatName}
                              autoFocus
                              onChange={(e) => setNewCatName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleAddCustomTag(); }
                                if (e.key === 'Escape') setIsCreatingCat(false);
                              }}
                            />
                            <button className="btn btn-success btn-sm" onClick={(e) => { e.stopPropagation(); handleAddCustomTag(); }}>
                              <i className="bi bi-check"></i>
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      plate.categories?.map(cat => (
                        <span key={cat.id} className="badge rounded-pill" style={{ backgroundColor: cat.color, color: contrastColor(cat.color), fontSize: '0.75rem' }}>
                          {cat.name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                </div>
                <div className="d-flex gap-1">
                  <button className="btn ez-button-light btn-sm" onClick={(e) => { e.stopPropagation(); handleToggleEdit(plate); }}><i className="bi bi-pencil"></i></button>
                  <button className="btn ez-button-light btn-sm" onClick={(e) => { e.stopPropagation(); setPlateToDelete(plate); setShowConfirm(true); }}><i className="bi bi-trash"></i></button>
                </div>
              </div>

              {/* Edit Form */}
              {isEditing && (
                <div className="p-2 border-top bg-light">
                  <div className="mb-2"><label>{t("Valid From")}</label>
                    <input type="date" className="form-control" value={editData.valid_from} onChange={(e) => setEditData({ ...editData, valid_from: e.target.value })} />
                  </div>
                  <div className="mb-2"><label>{t("Valid Until")}</label>
                    <input type="date" className="form-control" value={editData.valid_until} onChange={(e) => setEditData({ ...editData, valid_until: e.target.value })} />
                  </div>
                  <div className="mb-2"><label>{t("Note")}</label>
                    <input type="text" className="form-control" value={editData.note} onChange={(e) => setEditData({ ...editData, note: e.target.value })} />
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn ez-button-full btn-sm" onClick={handleSaveEdit}>{t("Save")}</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>{t("Cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          );
        };

        if (!groupBy) {
          return sortedPlates.map(plate => renderPlateCard(plate));
        }

        return groupedPlates && groupedPlates.map((group) => {
          const collapsed = !expandedGroups.has(group.key);
          return (
            <div key={group.key} className="mb-2">
              <div
                className="d-flex align-items-center px-3 py-2 mb-1 rounded"
                style={{
                  background: group.color + "18",
                  borderLeft: `4px solid ${group.color}`,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => toggleCollapsed(group.key)}
              >
                <i
                  className={`bi bi-chevron-${collapsed ? "right" : "down"} me-2`}
                  style={{ color: group.color, fontSize: "0.8rem" }}
                />
                <span className="fw-semibold" style={{ color: group.color, fontSize: "0.85rem" }}>
                  {group.label}
                </span>
                <span
                  className="ms-2 rounded-pill px-2"
                  style={{ fontSize: "0.72rem", background: group.color + "33", color: group.color, fontWeight: 600 }}
                >
                  {group.plates.length}
                </span>
                {(() => {
                  const groupIds = group.plates.map(p => p.id);
                  const selectedInGroup = groupIds.filter(id => selectedIds.has(id)).length;
                  // always-visible feedback (header shows even while collapsed) — without
                  // this, selecting a large group gives no sign it worked unless it's the
                  // one currently expanded, which won't be true when working through several
                  // groups in a row (and expanding a 200-plate group just to confirm doesn't scale)
                  return selectedInGroup > 0 && (
                    <span
                      className="ms-2 rounded-pill px-2 d-flex align-items-center gap-1"
                      style={{ fontSize: "0.72rem", background: "var(--ez-orange)", color: "#fff", fontWeight: 600 }}
                      title={t("plates_selected", { count: selectedInGroup })}
                    >
                      <svg width="9" height="9" viewBox="0 0 16 16">
                        <path d="M2 8l4 4 8-9" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {selectedInGroup}
                    </span>
                  );
                })()}
                <div style={{ flex: 1 }} />
                {(() => {
                  const groupIds = group.plates.map(p => p.id);
                  const allChecked = groupIds.every(id => selectedIds.has(id));
                  return (
                    <button
                      className="btn"
                      onClick={(e) => { e.stopPropagation(); selectGroupIds(groupIds, !allChecked); }}
                      title={t(allChecked ? "deselect_all_in_group" : "select_all_in_group")}
                      style={{
                        width: 28, height: 28, padding: 0, borderRadius: 7, flexShrink: 0,
                        border: `1.5px solid ${group.color}`,
                        background: allChecked ? group.color : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <rect x="1.5" y="1.5" width="13" height="13" rx="3" stroke={allChecked ? "#fff" : group.color} strokeWidth="1.4" />
                        {allChecked && <path d="M4 8.2l2.6 2.6L12.2 5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
                      </svg>
                    </button>
                  );
                })()}
              </div>
              {!collapsed && group.plates.map(plate => renderPlateCard(plate))}
            </div>
          );
        });
      })()}
      </div>

      {/* Bulk delete bar — sticky, only exists while something is checked */}
      {selectedIds.size > 0 && (
        <div
          className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 rounded-3 px-3 py-2 mt-3"
          style={{
            position: "sticky", bottom: 16, zIndex: 10,
            background: "#212529", color: "#f8f9fa",
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          }}
        >
          {/* basis 12rem: on a phone the count gets its own row and the buttons wrap below it, instead of running off-screen */}
          <span className="fw-semibold" style={{ fontSize: "0.9rem", flex: "1 1 12rem" }}>
            {t("plates_selected", { count: selectedIds.size })}
          </span>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <button className="btn btn-sm" style={{ color: "#f8f9fa", opacity: 0.8 }} onClick={exitSelectMode}>
              {t("Cancel")}
            </button>
            <button className="btn btn-sm ez-button-full d-flex align-items-center gap-1" onClick={() => setShowBulkEdit(true)}>
              <i className="bi bi-pencil"></i>{t("edit")}
            </button>
            <button className="btn btn-sm btn-danger d-flex align-items-center gap-1" onClick={() => setShowBulkConfirm(true)}>
              <i className="bi bi-trash"></i>{t("Delete")}
            </button>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      <ConfirmModal
        show={showConfirm}
        title={t("Confirm_Deletion")}
        onCancel={() => { setShowConfirm(false); setPlateToDelete(null); }}
        onConfirm={async () => { await handleDelete(plateToDelete); setShowConfirm(false); setPlateToDelete(null); }}
        confirmText={t("Delete")}
        cancelText={t("Cancel")}
      >
        <p>{t("Delete_question_plate")} <strong>{plateToDelete?.plate}</strong>?</p>
      </ConfirmModal>

      {/* Confirm Bulk Delete */}
      <ConfirmModal
        show={showBulkConfirm}
        title={t("Confirm_Deletion")}
        onCancel={() => setShowBulkConfirm(false)}
        onConfirm={handleBulkDelete}
        confirmText={t("Delete")}
        cancelText={t("Cancel")}
      >
        <p>{t("confirm_bulk_delete", { count: selectedIds.size })}</p>
        <ul className="mb-0">
          {plates.filter(p => selectedIds.has(p.id)).slice(0, 6).map(p => (
            <li key={p.id}>{p.plate}</li>
          ))}
          {selectedIds.size > 6 && (
            <li className="text-muted">{t("and_n_more", { count: selectedIds.size - 6 })}</li>
          )}
        </ul>
      </ConfirmModal>

      {/* Bulk Edit — dates only, on purpose: adding notes/categories here would
          need very different controls (additive, not "set to X") for a request
          nobody's made yet. Only the fields the admin opts into (checkbox) get sent. */}
      <ConfirmModal
        show={showBulkEdit}
        title={t("bulk_edit_title")}
        onCancel={resetBulkEditForm}
        onConfirm={handleBulkEditDates}
        confirmText={bulkEditSaving ? t("Loading...") : t("Save")}
        cancelText={t("Cancel")}
        confirmVariant="full"
      >
        <p className="text-muted small">{t("bulk_edit_hint", { count: selectedIds.size })}</p>

        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input" type="checkbox" id="bulkEditFromEnabled"
              checked={bulkEditFromEnabled}
              onChange={(e) => setBulkEditFromEnabled(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="bulkEditFromEnabled">{t("Valid From")}</label>
          </div>
          {bulkEditFromEnabled && (
            <input
              type="date" className="form-control form-control-sm mt-1"
              value={bulkEditFrom}
              onChange={(e) => setBulkEditFrom(e.target.value)}
            />
          )}
        </div>

        <div>
          <div className="form-check">
            <input
              className="form-check-input" type="checkbox" id="bulkEditUntilEnabled"
              checked={bulkEditUntilEnabled}
              onChange={(e) => setBulkEditUntilEnabled(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="bulkEditUntilEnabled">{t("Valid Until")}</label>
          </div>
          {bulkEditUntilEnabled && (
            <input
              type="date" className="form-control form-control-sm mt-1"
              value={bulkEditUntil}
              onChange={(e) => setBulkEditUntil(e.target.value)}
            />
          )}
        </div>
      </ConfirmModal>

    </div>
  );
}

export default PlateSelector;
